import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
  AccountCircle,
  Logout,
  Notifications as NotificationsIcon,
  LockReset,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import { Role } from '../../types';
import api from '../../utils/api';
import ChangePasswordDialog from '../Common/ChangePasswordDialog';

interface HeaderProps {
  onMenuClick: () => void;
  drawerWidth: string | number;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, drawerWidth }) => {
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // Fetch unread notification count for Super Admin
  useEffect(() => {
    if (user?.role !== Role.SUPER_ADMIN) {
      return;
    }

    const fetchUnreadCount = async () => {
      try {
        const response = await api.get<{ count: number }>('/notifications/unread-count');
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();
    // Poll every 30 seconds for new notifications
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleNotificationClick = () => {
    navigate('/notifications');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
        backgroundColor: (theme) =>
          theme.palette.mode === 'light'
            ? '#ffffff'
            : theme.palette.grey[900],
        color: (theme) =>
          theme.palette.mode === 'light' ? '#000' : '#fff',
        width: { xs: '100%', md: `calc(100% - ${typeof drawerWidth === 'string' ? drawerWidth : `${drawerWidth}px`})` },
        ml: { xs: 0, md: typeof drawerWidth === 'string' ? drawerWidth : `${drawerWidth}px` },
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <Toolbar sx={{ gap: isMobile ? 1 : 2, px: { xs: 1, sm: 2 } }}>
        {/* Mobile Menu Button */}
        <IconButton
          color="inherit"
          edge="start"
          onClick={onMenuClick}
          sx={{ 
            display: { xs: 'flex', md: 'none' },
            mr: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          aria-label="open menu"
        >
          <MenuIcon />
        </IconButton>

        {/* Logo and Title - Only show title on larger screens */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
            flexGrow: 1,
          }}
        >
          {!isSmallMobile && (
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.25rem' },
                background: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                    : 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
            </Typography>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          {/* Notifications - Super Admin Only */}
          {user?.role === Role.SUPER_ADMIN && (
            <IconButton
              color="inherit"
              onClick={handleNotificationClick}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              aria-label="notifications"
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}

          {/* Theme Toggle */}
          <IconButton
            color="inherit"
            onClick={toggleTheme}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'action.hover',
                transform: 'rotate(180deg)',
              },
            }}
            aria-label="toggle theme"
          >
            {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </IconButton>

          {/* User Menu */}
          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            aria-label="user menu"
          >
            <Avatar
              sx={{
                width: { xs: 32, sm: 36 },
                height: { xs: 32, sm: 36 },
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                fontWeight: 700,
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
              }}
            >
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: { xs: 240, sm: 280 },
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {user?.email}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'inline-block',
                  mt: 0.5,
                  px: 1,
                  py: 0.5,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  borderRadius: 0.5,
                  fontWeight: 600,
                }}
              >
                {user?.role}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <MenuItem
              onClick={handleMenuClose}
              sx={{
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <AccountCircle sx={{ mr: 1, fontSize: 20 }} /> Profile
            </MenuItem>
            {user?.role === Role.SUPER_ADMIN && (
              <MenuItem
                onClick={() => { handleMenuClose(); setChangePasswordOpen(true); }}
                sx={{
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <LockReset sx={{ mr: 1, fontSize: 20 }} /> Change Password
              </MenuItem>
            )}
            <MenuItem
              onClick={handleLogout}
              sx={{
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Logout sx={{ mr: 1, fontSize: 20 }} /> Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </AppBar>
  );
};

export default Header;
